import { CircleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { InvalidCell, LeftOutRows } from '@/services/api/structuredIngest'

/** How many bad values the screen lists; the counts always cover every one. */
const LISTED_CELLS = 10

function CellsNotice({
  heading,
  cells,
  more,
  withRow,
}: {
  heading: string
  cells: InvalidCell[]
  more: number
  withRow: boolean
}) {
  const { t } = useTranslation()
  return (
    <div role="status" className="flex flex-col gap-1 text-xs text-muted-foreground">
      <p className="flex items-center gap-1.5 font-medium text-foreground">
        <CircleAlert className="size-3.5 shrink-0" aria-hidden />
        {heading}
      </p>
      <ul className="flex flex-col gap-0.5 pl-5">
        {cells.map(cell => (
          <li key={`${cell.position}:${cell.column}:${cell.property}`}>
            {t(withRow ? 'tableMapping.leftOut.cell' : 'tableMapping.leftOut.rowCell', {
              row: cell.position + 1,
              column: cell.column,
              property: cell.property,
              value: cell.value,
              expected: t(`tableMapping.leftOut.expected.${cell.expected}`),
            })}
          </li>
        ))}
      </ul>
      {more > 0 && <p className="pl-5">{t('tableMapping.leftOut.more', { count: more })}</p>}
    </div>
  )
}

/** Rows a projection leaves out because a value does not fit its property's type (ADR-0081):
 *  announced by the preview before the write, reported by the projection after it. */
export function LeftOutRowsNotice({
  leftOut,
  written = false,
}: {
  leftOut: LeftOutRows
  written?: boolean
}) {
  const { t } = useTranslation()
  if (leftOut.rows === 0) return null
  const cells = leftOut.cells.slice(0, LISTED_CELLS)
  return (
    <CellsNotice
      heading={t(written ? 'tableMapping.leftOut.written' : 'tableMapping.leftOut.preview', {
        count: leftOut.rows,
      })}
      cells={cells}
      more={leftOut.cellCount - cells.length}
      withRow
    />
  )
}

/** The row lens: why the row on screen puts nothing into the case. */
export function LeftOutRowCells({ cells }: { cells: InvalidCell[] }) {
  const { t } = useTranslation()
  if (cells.length === 0) return null
  return (
    <CellsNotice heading={t('tableMapping.leftOut.lens')} cells={cells} more={0} withRow={false} />
  )
}
